<template>
  <div class="embed-wrapper">
    <BiometricAuthWidget
      :mode="mode"
      :config="widgetConfig"
    />
  </div>
</template>

<script>
import BiometricAuthWidget from '@/components/BiometricAuthWidget.vue'

export default {
  name: 'EmbedView',
  components: {
    BiometricAuthWidget
  },

  data() {
    return {
      mode: 'login',
      token: null,
      widgetConfig: {
        apiKey: '',
        userId: '',
        token: '',
        apiEndpoint: '/api/biometric',
        theme: {
          primaryColor: '#007AFF',
          secondaryColor: '#5856D6',
          errorColor: '#FF3B30',
          successColor: '#34C759',
          backgroundColor: '#FFFFFF',
          textColor: '#000000'
        },
        logo: null,
        onSuccess: this.handleSuccess,
        onError: this.handleError,
        onFallback: this.handleFallback
      }
    }
  },

  mounted() {
    this.parseUrlParams()
    this.setupPostMessage()
  },

  methods: {
    parseUrlParams() {
      const params = new URLSearchParams(window.location.search)
      
      // Token (e.g., ?token=v3.1783576783.6)
      if (params.has('token')) {
        this.token = params.get('token')
        this.widgetConfig.token = this.token
      }
      
      // Mode
      if (params.has('mode')) {
        this.mode = params.get('mode')
      }
      
      // Basic config
      if (params.has('userId')) {
        this.widgetConfig.userId = params.get('userId')
      }
      
      if (params.has('apiKey')) {
        this.widgetConfig.apiKey = params.get('apiKey')
      }
      
      if (params.has('apiEndpoint')) {
        this.widgetConfig.apiEndpoint = params.get('apiEndpoint')
      }
      
      // Theme
      if (params.has('primaryColor')) {
        this.widgetConfig.theme.primaryColor = '#' + params.get('primaryColor').replace('#', '')
      }
      
      if (params.has('secondaryColor')) {
        this.widgetConfig.theme.secondaryColor = '#' + params.get('secondaryColor').replace('#', '')
      }
      
      if (params.has('errorColor')) {
        this.widgetConfig.theme.errorColor = '#' + params.get('errorColor').replace('#', '')
      }
      
      if (params.has('successColor')) {
        this.widgetConfig.theme.successColor = '#' + params.get('successColor').replace('#', '')
      }
      
      if (params.has('backgroundColor')) {
        this.widgetConfig.theme.backgroundColor = '#' + params.get('backgroundColor').replace('#', '')
      }
      
      if (params.has('textColor')) {
        this.widgetConfig.theme.textColor = '#' + params.get('textColor').replace('#', '')
      }
      
      // Logo
      if (params.has('logo')) {
        this.widgetConfig.logo = decodeURIComponent(params.get('logo'))
      }
    },

    setupPostMessage() {
      // Listen for configuration updates from parent window
      window.addEventListener('message', (event) => {
        if (event.data.type === 'biometric-config-update') {
          const config = event.data.config
          
          if (config.mode) this.mode = config.mode
          if (config.token) {
            this.token = config.token
            this.widgetConfig.token = config.token
          }
          if (config.userId) this.widgetConfig.userId = config.userId
          if (config.apiKey) this.widgetConfig.apiKey = config.apiKey
          if (config.apiEndpoint) this.widgetConfig.apiEndpoint = config.apiEndpoint
          if (config.theme) Object.assign(this.widgetConfig.theme, config.theme)
          if (config.logo) this.widgetConfig.logo = config.logo
        }
      })
    },

    handleSuccess(data) {
      this.postMessageToParent('biometric-success', this.sanitizeData(data))
    },

    handleError(error) {
      this.postMessageToParent('biometric-error', this.sanitizeData(error))
    },

    handleFallback(data) {
      this.postMessageToParent('biometric-fallback', this.sanitizeData(data))
    },

    sanitizeData(data) {
      // Deep clone and sanitize data to remove functions, circular references, etc.
      // Only send plain serializable data through postMessage
      try {
        return JSON.parse(JSON.stringify(data))
      } catch (err) {
        // If JSON stringification fails (circular refs), manually extract safe properties
        if (typeof data === 'object' && data !== null) {
          const sanitized = {}
          for (const key in data) {
            const value = data[key]
            // Only include primitives and plain objects/arrays
            if (
              value === null ||
              typeof value === 'string' ||
              typeof value === 'number' ||
              typeof value === 'boolean' ||
              Array.isArray(value)
            ) {
              sanitized[key] = value
            } else if (typeof value === 'object') {
              // Try to stringify nested object
              try {
                sanitized[key] = JSON.parse(JSON.stringify(value))
              } catch {
                // Skip non-serializable nested objects
                sanitized[key] = String(value)
              }
            }
          }
          return sanitized
        }
        return { error: String(data) }
      }
    },

    postMessageToParent(type, data) {
      if (window.parent !== window) {
        window.parent.postMessage({
          type,
          data,
          timestamp: new Date().toISOString()
        }, '*')
      }
    }
  }
}
</script>

<style scoped>
.embed-wrapper {
  width: 100%;
  height: 100vh;
  margin: 0;
  padding: 0;
  overflow: hidden;
}
</style>
